import { editorTextBoxFuneralHomeLimitPageEntry } from '@/components/admin/EditorTextBoxFuneralHomeLimit/editorTextBoxFuneralHomeLimitPageEntry'
import { apiDocsPageEntry } from '@/components/admin/ApiDocs/apiDocsPageEntry'
import { transparentPrintBackgroundPageEntry } from '@/components/admin/TransparentPrintBackground/transparentPrintBackgroundPageEntry'

/**
 * The admin tools, grouped in display order, with a matcher from a pathname
 * to its page. Only pages with content are listed; the placeholder routes
 * (analytics, assets, products, status message, themes) stay unlisted until
 * they have some.
 */

export type AdminPage = {
  label: string
  path: string
  description: string
}

export type AdminPageGroup = {
  label: string
  pages: readonly AdminPage[]
}

export const ADMIN_PAGE_GROUPS: readonly AdminPageGroup[] = [
  {
    label: 'Print',
    pages: [
      {
        label: 'PDF Jobs',
        path: '/admin/pdf-jobs',
        description:
          'Track every proof and final PDF render, stop a stuck job and resubmit a failed one.',
      },
      {
        label: 'MessengerPress',
        path: '/admin/messenger-press',
        description:
          'Pick a source PDF, adjust its print recipe and run it through the print pipeline.',
      },
      {
        label: transparentPrintBackgroundPageEntry.title,
        path: transparentPrintBackgroundPageEntry.path,
        description: transparentPrintBackgroundPageEntry.description,
      },
    ],
  },
  {
    label: 'Cases',
    pages: [
      {
        label: 'Case Image QC',
        path: '/admin/case-image-qc',
        description:
          "Look up a case or order and compare each case image's original with its enhanced output.",
      },
    ],
  },
  {
    label: 'Editor',
    pages: [
      {
        label: editorTextBoxFuneralHomeLimitPageEntry.title,
        path: editorTextBoxFuneralHomeLimitPageEntry.path,
        description: editorTextBoxFuneralHomeLimitPageEntry.description,
      },
    ],
  },
  {
    label: 'Products and themes',
    pages: [
      {
        label: 'Launch',
        path: '/admin/launch',
        description:
          'Copy themes, verses and layouts, create products and validate them for launch.',
      },
    ],
  },
  {
    label: 'Developers',
    pages: [
      {
        label: apiDocsPageEntry.title,
        path: apiDocsPageEntry.path,
        description: apiDocsPageEntry.description,
      },
    ],
  },
]

/**
 * A pathname in the form the app's exact admin routes match it by: lowercase,
 * without one trailing slash.
 */
export const normalizeAdminPath = (pathname: string): string =>
  pathname.toLowerCase().replace(/\/$/, '')

/**
 * The listed page a pathname opens, or null. It matches the whole path,
 * ignoring case and one trailing slash, as the app's exact admin routes do.
 * The admin home itself opens no listed page.
 */
export const adminPageForPath = (pathname: string): AdminPage | null => {
  const target = normalizeAdminPath(pathname)
  return (
    ADMIN_PAGE_GROUPS.flatMap((group) => group.pages).find(
      (page) => page.path === target,
    ) ?? null
  )
}
